/**
 * V2 Chat Service - Direct Integration with Real-time Subscriptions
 * Replaces Firebase Functions with direct Firestore access + real-time updates
 */

import { createSignal, onCleanup } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { queryKeys } from "@core/query";
import { chatService, userService, bookingService } from "@core/services-v2";
import type { Chat, Message, User, Booking } from "@core/services-v2";
import type {
  ChatSummary,
  ChatMessage as ChatMessageType,
  ChatProfile,
  UserSummary,
  BookingSummary,
  SendChatMessagePayload
} from "./types";

// Real-time subscription signals
const [chats, setChats] = createSignal<ChatSummary[]>([]);
const [chatMessages, setChatMessages] = createSignal<Map<string, ChatMessageType[]>>(new Map());

// Subscription management
let chatsUnsubscribe: (() => void) | null = null;
const messageUnsubscribes = new Map<string, () => void>();

const mapChatToSummary = (chat: Chat): ChatSummary => ({
  id: chat.id!,
  status: chat.status as any,
  userId: chat.userId || null,
  anonSessionId: chat.anonSessionId || null,
  lastMessagePreview: chat.lastMessage?.preview || null,
  lastMessageSenderType: chat.lastMessage?.senderType as any || null,
  lastMessageAt: chat.lastMessageAt || chat.updatedAt,
  unreadForAdmin: chat.unreadCounts?.admin || 0,
  updatedAt: chat.updatedAt,
  createdAt: chat.createdAt,
});

const mapChatMessageToType = (message: Message): ChatMessageType => ({
  id: message.id!,
  senderType: message.senderType as any,
  senderId: message.senderId || null,
  text: message.content || '', // Message uses 'content' field
  createdAt: message.createdAt,
  deliveryStatus: "sent", // V2 Message doesn't have deliveryStatus
  metadata: message.metadata, // Pass through metadata including actions
});

const mapUserToSummary = (user: User): UserSummary => ({
  id: user.uid,
  displayName: user.displayName || user.email || null,
  email: user.email || null,
  phoneNumber: user.phoneNumber || null,
  photoURL: user.photoURL || null,
  stats: user.stats ? {
    totalCleanings: user.stats.totalBookings || undefined,
    totalSquareFootCleaned: user.stats.averageRating || undefined,
  } : null,
});

const mapBookingToSummary = (booking: Booking): BookingSummary => ({
  id: booking.id!,
  status: booking.status,
  serviceId: booking.serviceId || null,
  roomType: booking.roomType || null,
  amount: booking.amount || null,
  currency: booking.currency || null,
  createdAt: booking.createdAt,
  scheduledDate: booking.schedule?.scheduledDate || null,
  timeSlotLabel: booking.schedule?.timeSlotLabel || null,
});

// Initialize real-time subscriptions
export const initializeChatSubscriptions = () => {
  // Subscribe to all chats with real-time updates
  if (chatsUnsubscribe) {
    chatsUnsubscribe();
  }

  chatsUnsubscribe = chatService.subscribeToChats((updatedChats) => {
    const chatSummaries = updatedChats
      .map(mapChatToSummary)
      .sort((a, b) => {
        const aTime = a.lastMessageAt?.getTime() || 0;
        const bTime = b.lastMessageAt?.getTime() || 0;
        return bTime - aTime; // Most recent first
      });

    setChats(chatSummaries);
    console.log(`📨 Real-time chat update: ${chatSummaries.length} chats`);
  });

  console.log('🔄 Chat real-time subscriptions initialized');
};

// Subscribe to messages for a specific chat
export const subscribeToChatMessages = (chatId: string) => {
  // Clean up existing subscription for this chat
  const existingUnsubscribe = messageUnsubscribes.get(chatId);
  if (existingUnsubscribe) {
    existingUnsubscribe();
  }

  const unsubscribe = chatService.subscribeToChatMessages(chatId, (messages) => {
    const messageTypes = messages.map(mapChatMessageToType);
    setChatMessages(prev => new Map(prev.set(chatId, messageTypes)));
    console.log(`💬 Real-time messages update for chat ${chatId}: ${messageTypes.length} messages`);
  });

  messageUnsubscribes.set(chatId, unsubscribe);
  return unsubscribe;
};

// Cleanup all subscriptions
export const cleanupChatSubscriptions = () => {
  if (chatsUnsubscribe) {
    chatsUnsubscribe();
    chatsUnsubscribe = null;
  }

  messageUnsubscribes.forEach(unsubscribe => unsubscribe());
  messageUnsubscribes.clear();

  console.log('🧹 Chat subscriptions cleaned up');
};

// Auto-cleanup on module unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', cleanupChatSubscriptions);
}

export const fetchRecentChats = async (opts?: { status?: string; limit?: number }): Promise<ChatSummary[]> => {
  try {
    // Initialize subscriptions if not already done
    if (!chatsUnsubscribe) {
      initializeChatSubscriptions();
    }

    // Return current real-time data if available
    const currentChats = chats();
    if (currentChats.length > 0) {
      return currentChats;
    }

    // Fallback to direct fetch if no real-time data yet
    const chatList = await chatService.getAllChats(opts?.limit);
    return chatList.map(mapChatToSummary);
  } catch (error) {
    console.error('Error fetching chats:', error);
    throw error;
  }
};

export const fetchChatMessages = async (chatId: string): Promise<ChatMessageType[]> => {
  try {
    // Subscribe to real-time updates for this chat
    subscribeToChatMessages(chatId);

    // Check if we have real-time data
    const currentMessages = chatMessages().get(chatId);
    if (currentMessages) {
      return currentMessages;
    }

    // Fallback to direct fetch
    const messages = await chatService.getChatMessages(chatId);
    return messages.map(mapChatMessageToType);
  } catch (error) {
    console.error('Error fetching chat messages:', error);
    throw error;
  }
};

export const fetchChatById = async (chatId: string): Promise<ChatSummary | null> => {
  try {
    const chat = await chatService.getChat(chatId);
    return chat ? mapChatToSummary(chat) : null;
  } catch (error) {
    console.error('Error fetching chat:', error);
    return null;
  }
};

export const fetchChatProfile = async (chatId: string): Promise<ChatProfile | null> => {
  try {
    const chat = await fetchChatById(chatId);
    if (!chat) return null;

    if (!chat.userId) {
      return { chat, user: null, bookings: [] };
    }

    // Fetch user and bookings in parallel
    const [user, bookings] = await Promise.all([
      userService.getUser(chat.userId).then(u => u ? mapUserToSummary(u) : null),
      bookingService.getBookingsByUser(chat.userId).then(bs => bs.map(mapBookingToSummary))
    ]);

    return { chat, user, bookings };
  } catch (error) {
    console.error('Error fetching chat profile:', error);
    return null;
  }
};

export const sendChatMessage = async (payload: SendChatMessagePayload): Promise<ChatMessageType> => {
  try {
    // Use V2 service to create message directly in Firestore
    const message = await chatService.createMessage({
      chatId: payload.chatId,
      content: payload.text,
      senderType: 'admin',
      senderId: 'admin', // TODO: Get actual admin user ID
      type: 'text',
    });

    return mapChatMessageToType(message);
  } catch (error) {
    console.error('Error sending chat message:', error);
    throw error;
  }
};

// Real-time query hooks
export const useChatListQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.chats.root(),
    queryFn: () => fetchRecentChats(),
    // Enable automatic refetching for real-time feel
    refetchInterval: 30000, // Backup polling every 30s
    refetchOnWindowFocus: true,
  }));

export const useChatMessagesQuery = (chatId: () => string | null) =>
  createQuery(() => {
    const id = chatId();
    return {
      queryKey: queryKeys.chats.messages(id ?? "pending"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchChatMessages(id) : Promise.resolve([] as ChatMessageType[])),
      // Real-time updates via subscriptions, but backup polling
      refetchInterval: 10000, // Backup polling every 10s
      refetchOnWindowFocus: true,
    };
  });

export const useChatProfileQuery = (chatId: () => string | null) =>
  createQuery(() => {
    const id = chatId();
    return {
      queryKey: queryKeys.chats.detail(id ?? "profile"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchChatProfile(id) : Promise.resolve(null)),
    };
  });

export const useChatSummaryQuery = useChatProfileQuery;

// Real-time signals for components
export { chats, chatMessages };